import { useCallback } from "react";
import { useMutation } from "@apollo/client/react";
import { notifications } from "@mantine/notifications";
import { IconCheck } from "@tabler/icons-react";
import { UPDATE_SEEDING_PINS_MUTATION } from "./seeding.operations";
import {
  type SeedingPinState,
  toSeedingPinInput,
} from "./seeding.types";

interface UseSavePinsOptions {
  readonly stepId: string;
  readonly successTitle: string;
  readonly successMessage: string;
  readonly onSaved?: (pins: readonly SeedingPinState[]) => void;
}

interface UseSavePinsResult {
  readonly save: (pins: readonly SeedingPinState[]) => Promise<void>;
  readonly saving: boolean;
}

/**
 * Shared hook encapsulating the pin-save mutation used by both the group-pin
 * and slot-pin tabs. Centralizes payload normalization, error surfacing via
 * Mantine notifications and the optional onSaved callback so callers only
 * describe *what* they are saving, not *how*.
 */
export const useSavePins = ({
  stepId,
  successTitle,
  successMessage,
  onSaved,
}: UseSavePinsOptions): UseSavePinsResult => {
  const [updateSeedingPins, { loading: saving }] = useMutation(
    UPDATE_SEEDING_PINS_MUTATION,
  );

  const save = useCallback(
    async (pins: readonly SeedingPinState[]): Promise<void> => {
      try {
        const result = await updateSeedingPins({
          variables: {
            stepId,
            pins: pins.map(toSeedingPinInput),
          },
        });
        if (result?.error) {
          throw new Error(result.error.message);
        }
        notifications.show({
          title: successTitle,
          message: successMessage,
          color: "green",
          icon: <IconCheck size={16} />,
        });
        onSaved?.(pins);
      } catch (err) {
        notifications.show({
          title: "Failed to save pins",
          message: err instanceof Error ? err.message : "Unexpected error",
          color: "red",
        });
      }
    },
    [stepId, successTitle, successMessage, onSaved, updateSeedingPins],
  );

  return { save, saving };
};
